/**
 * ReportsView Component
 *
 * Report workspace with:
 * - Top selector bar: Account, Project, Run
 * - Collapsible left sidebar for report navigation
 * - Large right content area that loads the selected report
 *
 * Data source: GET /workflows/runs?account_id={id}&limit=100
 * Each workflow run contains a job_ids map (pipeline node type → job ID).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'

// Parse URL deep-link params once at module load (set before React renders)
const URL_INIT = (() => {
  const p = new URLSearchParams(window.location.search)
  return {
    accountId: p.get('account_id') || '',
    appName:   p.get('app_name')   || '',
    runId:     p.get('run_id')     || '',
    stage:     p.get('stage')      || '',
  }
})()
import {
  FileBarChart, Database, TrendingUp,
  ChevronDown, Loader2, AlertCircle,
  BarChart3, ChevronRight, Layers,
  PanelLeftClose, PanelLeftOpen, Play
} from 'lucide-react'
import { useApplications } from '../../hooks'
import { getAPIBaseUrl } from '../../environment'
import AIAssistant from '../designer/AIAssistant'
import MapView from '../views/MapView'
import DecomposeView from '../views/DecomposeView'
import DataView from '../views/DataView'
import DiscoveryView from '../views/DiscoveryView'
import ArchitectView from '../views/ArchitectView'
import RefactorReportView from '../views/RefactorReportView'
import AnalyzeView from '../views/AnalyzeView'
import CodeInventoryView from '../views/CodeInventoryView'
import DataDictionaryView from '../views/DataDictionaryView'
import ExecutiveSummaryView from '../views/ExecutiveSummaryView'
import AiNamingReportView from '../views/AiNamingReportView'
import HitlOverviewReport from './HitlOverviewReport'
import RunOverviewDashboard from './RunOverviewDashboard'
import './ReportsView.css'

// ── Pipeline node type → report flow type mapping ───────────────────────────
//
// Workflow runs store job_ids keyed by pipeline node type (e.g.
// "pipeline-code-analysis"). The report nav uses shorter flow type IDs
// (e.g. "codeanalysis"). This map bridges the two naming systems.
//
const PIPELINE_NODE_MAP = {
  'codeanalysis':      'pipeline-code-analysis',
  'ainaming':          'pipeline-ai-naming',
  'dependencymapper':  'pipeline-dependencies',
  'dataanalysis':      'pipeline-data',
  'discovery':         'pipeline-discovery',
  'architecture':      'pipeline-architecture',
  'coderefactor':      'pipeline-refactor',
  'monolithidentifier':'pipeline-monolith',
}

// ── Left sidebar navigation groups + items ──────────────────────────────────

const REPORT_NAV_GROUPS = [
  {
    id: 'analyze',
    label: 'Analyze',
    icon: BarChart3,
    items: [
      { id: 'codeanalysis',       label: 'Code Analysis',       view: 'analyze',         requiredJob: 'codeanalysis'    },
      { id: 'ainaming',           label: 'AI Naming',           view: 'ai-naming',       requiredJob: 'ainaming'        },
      { id: 'code-inventory',     label: 'Code Inventory',      view: 'code-inventory',  requiredJob: 'codeanalysis'    },
      { id: 'data-dictionary',    label: 'Data Dictionary',     view: 'data-dictionary', requiredJob: 'codeanalysis'    },
      { id: 'coderefactor',       label: 'Code Refactor',       view: 'refactor-report', requiredJob: 'coderefactor'    },
      { id: 'dependencymapper',   label: 'Dependency Mapper',   view: 'map',             requiredJob: 'codeanalysis'    },
      { id: 'monolithidentifier', label: 'Monolith Identifier', view: 'decompose',       requiredJob: 'monolithidentifier' },
      { id: 'dataanalysis',       label: 'Data Analysis',       view: 'data',            requiredJob: 'dataanalysis'    },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    icon: TrendingUp,
    items: [
      { id: 'discovery',         label: 'Discovery & ROI',   view: 'discovery',        requiredJob: 'discovery'    },
      { id: 'architecture',      label: 'Architecture',      view: 'architect',        requiredJob: 'architecture' },
      { id: 'executive-summary', label: 'Executive Summary', view: 'executive-summary',requiredJob: 'discovery'    },
    ],
  },
]

// ── Journey (HITL) nav items — mirrors Analyze, uses linked job IDs ─────────
// linkedJobKey: which field from linked_jobs to use as the primary jobId
// 'rcJobId' = the HITL refactor_code job itself

const JOURNEY_NAV_ITEMS = [
  { id: 'j-codeanalysis',    label: 'Code Analysis',    view: 'analyze',          linkedJobKey: 'ca_job_id'        },
  { id: 'j-ainaming',        label: 'AI Naming',        view: 'ai-naming',        linkedJobKey: 'ai_naming_job_id' },
  { id: 'j-code-inventory',  label: 'Code Inventory',   view: 'code-inventory',   linkedJobKey: 'ca_job_id'        },
  { id: 'j-data-dictionary', label: 'Data Dictionary',  view: 'data-dictionary',  linkedJobKey: 'ca_job_id'        },
  { id: 'j-dependencies',    label: 'Dependencies',     view: 'map',              linkedJobKey: 'ca_job_id'        },
  { id: 'hitl-overview',     label: 'Code Health',      view: 'hitl-overview',    linkedJobKey: null               },
]

// ── View component map ───────────────────────────────────────────────────────

const VIEW_COMPONENTS = {
  'analyze':           AnalyzeView,
  'ai-naming':         AiNamingReportView,
  'map':               MapView,
  'decompose':         DecomposeView,
  'data':              DataView,
  'discovery':         DiscoveryView,
  'architect':         ArchitectView,
  'refactor-report':   RefactorReportView,
  'code-inventory':    CodeInventoryView,
  'data-dictionary':   DataDictionaryView,
  'executive-summary': ExecutiveSummaryView,
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Auth header for API calls — matches pattern used in RunsView */
function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Build a human-readable label for a workflow run.
 * Prefers display_name, then parses a timestamp from the run ID,
 * then falls back to the last 8 chars of the run ID.
 */
function runLabel(run, index) {
  if (run.display_name) return run.display_name

  // Try to parse Unix timestamp from the tail of the run ID
  // e.g. "wfrun_acc_12345_AppName_1774001675"
  const tail = (run.workflow_run_id || '').split('_').pop()
  if (/^\d{10}$/.test(tail)) {
    const d = new Date(parseInt(tail, 10) * 1000)
    return (
      d.toLocaleDateString() +
      ' ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    )
  }

  // Try created_at
  if (run.created_at) {
    const d = new Date(run.created_at)
    return (
      d.toLocaleDateString() +
      ' ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    )
  }

  // Fall back to the workflow_name + last 8 chars of run ID
  const short = (run.workflow_run_id || '').slice(-8)
  return run.workflow_name ? `${run.workflow_name} (${short})` : short || `Run ${index + 1}`
}

// ── Component ────────────────────────────────────────────────────────────────

function ReportsView() {
  const { accounts, applications, loading: appsLoading } = useApplications()

  // Selector state — initialized from URL deep-link params if present
  const [selectedAccountId, setSelectedAccountId] = useState(URL_INIT.accountId)
  const [selectedAppName,   setSelectedAppName]   = useState(URL_INIT.appName)
  const [selectedRunId,     setSelectedRunId]     = useState(URL_INIT.runId)

  // When selectedAppName is set from URL params on first render, we must
  // restore the run ID after the reset-on-appname-change effect fires.
  const pendingRunId = useRef(URL_INIT.runId)

  // Whether the stage deep-link auto-navigation has already fired
  const stageNavigated = useRef(false)

  // Workflow runs for the selected account (all runs, then filtered by project)
  const [allRuns,     setAllRuns]     = useState([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [runsError,   setRunsError]   = useState(null)

  // ── HITL Journey run state ────────────────────────────────────────────────
  const [hitlJobs,          setHitlJobs]         = useState([])
  const [hitlJobsLoading,   setHitlJobsLoading]  = useState(false)
  const [selectedHitlJobId, setSelectedHitlJobId] = useState('')
  // linked_jobs for the currently-selected HITL job (ca_job_id, ai_naming_job_id, etc.)
  const [hitlLinkedJobs,    setHitlLinkedJobs]   = useState({})

  // Sidebar + nav state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({
    analyze: true, business: true, journey: true,
  })

  // Active view — rendered in the right panel
  const [activeView,    setActiveView]   = useState(null)
  const [activeNavItem, setActiveNavItem] = useState(null)
  const [viewContext,   setViewContext]  = useState(null)

  // ── Account / Project lists ───────────────────────────────────────────────

  const uniqueAccounts = useMemo(() => {
    if (accounts?.length > 0) {
      return accounts.map(a => ({
        id: a.accountId || a.id,
        name: a.accountName || a.name || a.accountId || a.id,
      }))
    }
    // Fallback: derive unique accounts from applications list
    const map = new Map()
    applications?.forEach(app => {
      // Handle both field names returned by different DB adapters
      const acctId = app.scoutAccountId || app.account_id
      if (acctId && !map.has(acctId)) {
        map.set(acctId, { id: acctId, name: app.accountName || acctId })
      }
    })
    return Array.from(map.values())
  }, [accounts, applications])

  const filteredApps = useMemo(() => {
    if (!selectedAccountId) return []
    return (applications || []).filter(app => {
      const acctId = app.scoutAccountId || app.account_id
      return acctId === selectedAccountId
    })
  }, [applications, selectedAccountId])

  // ── Fetch workflow runs when account changes ──────────────────────────────
  //
  // The real source of truth is /workflows/runs?account_id={id}&limit=100.
  // This returns all runs for the account; we filter by workflow_name locally
  // to show only runs for the selected project.
  //
  useEffect(() => {
    if (!selectedAccountId) {
      setAllRuns([])
      setSelectedRunId('')
      return
    }

    const fetchRuns = async () => {
      setRunsLoading(true)
      setRunsError(null)
      try {
        const apiBase = getAPIBaseUrl()
        const res = await fetch(
          `${apiBase}/workflows/runs?account_id=${encodeURIComponent(selectedAccountId)}&limit=100`,
          { headers: authHeaders() }
        )
        if (!res.ok) throw new Error(`Failed to fetch runs: ${res.status}`)
        const data = await res.json()
        setAllRuns(data.workflow_runs || [])
      } catch (err) {
        console.error('[ReportsView] Failed to fetch workflow runs:', err)
        setRunsError(err.message)
        setAllRuns([])
      } finally {
        setRunsLoading(false)
      }
    }

    fetchRuns()
  }, [selectedAccountId])

  // ── Fetch HITL Journey jobs when account + project change ────────────────
  useEffect(() => {
    if (!selectedAccountId || !selectedAppName) {
      setHitlJobs([])
      setSelectedHitlJobId('')
      return
    }
    const userId = JSON.parse(localStorage.getItem('modernizeit_user') || '{}')?.user_id || ''
    if (!userId) return
    setHitlJobsLoading(true)
    const apiBase = getAPIBaseUrl()
    fetch(
      `${apiBase}/refactor-code/completed-jobs?account_id=${encodeURIComponent(selectedAccountId)}&user_id=${encodeURIComponent(userId)}&application_name=${encodeURIComponent(selectedAppName)}`,
      { headers: authHeaders() }
    )
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const jobs = Array.isArray(data) ? data : []
        setHitlJobs(jobs)
        if (jobs.length > 0) {
          const first = jobs[0]
          setSelectedHitlJobId(first.job_id)
          setHitlLinkedJobs(first.linked_jobs || {})
          setActiveView('hitl-overview')
          setActiveNavItem('hitl-overview')
        }
      })
      .catch(() => setHitlJobs([]))
      .finally(() => setHitlJobsLoading(false))
  }, [selectedAccountId, selectedAppName])

  // When the project changes, reset the selected run.
  // On the first render (URL deep-link), restore the URL run ID instead of clearing.
  useEffect(() => {
    if (pendingRunId.current) {
      setSelectedRunId(pendingRunId.current)
      pendingRunId.current = ''
    } else {
      setSelectedRunId('')
    }
  }, [selectedAppName])

  // ── Runs filtered to the selected project ────────────────────────────────

  const projectRuns = useMemo(() => {
    if (!selectedAppName || allRuns.length === 0) return []
    return allRuns.filter(r => r.workflow_name === selectedAppName)
  }, [allRuns, selectedAppName])

  const selectedRun = useMemo(() => {
    if (!selectedRunId) return null
    return projectRuns.find(r => r.workflow_run_id === selectedRunId) || null
  }, [projectRuns, selectedRunId])

  // Auto-select first run when project runs load
  useEffect(() => {
    if (projectRuns.length > 0 && !selectedRunId) {
      setSelectedRunId(projectRuns[0].workflow_run_id)
    }
  }, [projectRuns, selectedRunId])

  // ── Job lookup from selected run's job_ids ────────────────────────────────
  //
  // job_ids is keyed by pipeline node type (e.g. "pipeline-code-analysis").
  // PIPELINE_NODE_MAP translates the report flow type to the pipeline key.
  // Returns a synthetic job object { flow_type, job_id } or null.
  //
  const getJobForType = useCallback((flowType) => {
    if (!selectedRun?.job_ids) return null
    const pipelineKey = PIPELINE_NODE_MAP[flowType]
    if (!pipelineKey) return null
    const jobId = selectedRun.job_ids[pipelineKey]
    return jobId ? { flow_type: flowType, job_id: jobId } : null
  }, [selectedRun])

  // ── Report click → build context → open view ─────────────────────────────

  const handleReportClick = useCallback((item) => {
    if (!selectedRun) return
    const requiredJob = getJobForType(item.requiredJob || item.id)
    if (!requiredJob) return

    const ids = selectedRun.job_ids || {}

    // Resolve all job IDs directly from job_ids map
    const caJobId    = ids[PIPELINE_NODE_MAP['codeanalysis']]
    const aiNamJobId = ids[PIPELINE_NODE_MAP['ainaming']]
    const dmJobId    = ids[PIPELINE_NODE_MAP['dependencymapper']]
    const daJobId    = ids[PIPELINE_NODE_MAP['dataanalysis']]
    const discJobId  = ids[PIPELINE_NODE_MAP['discovery']]
    const archJobId  = ids[PIPELINE_NODE_MAP['architecture']]
    const refrJobId  = ids[PIPELINE_NODE_MAP['coderefactor']]
    const miJobId    = ids[PIPELINE_NODE_MAP['monolithidentifier']]

    // map and decompose views need the codeanalysis job as their primary
    let primaryJobId = requiredJob.job_id
    if (item.view === 'map' || item.view === 'decompose') {
      primaryJobId = caJobId || requiredJob.job_id
    }

    const context = {
      jobs: {
        codeanalysis_job_id:      caJobId,
        ai_naming_job_id:         aiNamJobId,
        dependencymapper_job_id:  dmJobId,
        dataanalysis_job_id:      daJobId,
        discovery_job_id:         discJobId,
        architecture_job_id:      archJobId,
        coderefactor_job_id:      refrJobId,
        monolith_identifier_job_id: miJobId,
      },
      jobId:                primaryJobId,
      baseName:             selectedAppName.toLowerCase(),
      mainProgram:          selectedAppName,
      dependencyMapperJobId:dmJobId,
    }

    setViewContext(context)
    setActiveView(item.view)
    setActiveNavItem(item.id)
  }, [selectedRun, getJobForType, selectedAppName])

  // ── Journey (HITL) report click — builds context from linked_jobs ───────────
  const handleJourneyReportClick = useCallback((item) => {
    if (item.view === 'hitl-overview') {
      setViewContext(null)
      setActiveView('hitl-overview')
      setActiveNavItem(item.id)
      return
    }

    // Need at least the CA job to show anything
    const caJobId     = hitlLinkedJobs.ca_job_id     || null
    const aiNamJobId  = hitlLinkedJobs.ai_naming_job_id || null
    const dmJobId     = hitlLinkedJobs.dm_job_id     || null

    // Primary job for this item
    const primaryJobId = item.linkedJobKey === null
      ? null
      : item.linkedJobKey === 'ca_job_id'
        ? caJobId
        : item.linkedJobKey === 'ai_naming_job_id'
          ? aiNamJobId
          : item.linkedJobKey === 'dm_job_id'
            ? dmJobId
            : null

    if (!primaryJobId) return  // not available — button should be disabled

    const context = {
      jobs: {
        codeanalysis_job_id:     caJobId,
        ai_naming_job_id:        aiNamJobId,
        dependencymapper_job_id: dmJobId,
      },
      jobId:                primaryJobId,
      baseName:             selectedAppName.toLowerCase(),
      mainProgram:          selectedAppName,
      dependencyMapperJobId:dmJobId,
    }

    setViewContext(context)
    setActiveView(item.view)
    setActiveNavItem(item.id)
  }, [hitlLinkedJobs, selectedAppName])

  const handleBack = useCallback(() => {
    setActiveView(null)
    setActiveNavItem(null)
    setViewContext(null)
  }, [])

  // Auto-navigate to a specific report stage when opened via deep link.
  // Fires once after selectedRun becomes available and jobs are loaded.
  useEffect(() => {
    if (!URL_INIT.stage || !selectedRun || stageNavigated.current) return
    for (const group of REPORT_NAV_GROUPS) {
      const item = group.items.find(i => i.id === URL_INIT.stage)
      if (item) {
        handleReportClick(item)
        stageNavigated.current = true
        break
      }
    }
  }, [selectedRun, handleReportClick])

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }))
  }, [])

  // ── Derived state for rendering ──────────────────────────────────────────

  const hasProjectSelection = !!(selectedAccountId && selectedAppName)
  const hasRunSelection     = !!selectedRun

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="reports-workspace">

      {/* ── Top selector bar ──────────────────────────────────────── */}
      <div className="reports-selector-bar">

        {/* Account */}
        <div className="rs-selector-group">
          <label>Account</label>
          <div className="rs-select-wrapper">
            <select
              value={selectedAccountId}
              onChange={e => {
                setSelectedAccountId(e.target.value)
                setSelectedAppName('')
                setSelectedRunId('')
                setAllRuns([])
              }}
              disabled={appsLoading}
            >
              <option value="">Select account…</option>
              {uniqueAccounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <ChevronDown size={13} className="rs-select-icon" />
          </div>
        </div>

        <span className="rs-separator">›</span>

        {/* Project */}
        <div className="rs-selector-group">
          <label>Project</label>
          <div className="rs-select-wrapper">
            <select
              value={selectedAppName}
              onChange={e => setSelectedAppName(e.target.value)}
              disabled={!selectedAccountId || appsLoading}
            >
              <option value="">Select project…</option>
              {filteredApps.map(app => (
                <option key={app.id} value={app.name}>{app.name}</option>
              ))}
            </select>
            <ChevronDown size={13} className="rs-select-icon" />
          </div>
        </div>

        <span className="rs-separator">›</span>

        {/* Run */}
        <div className="rs-selector-group">
          <label>Run</label>
          <div className="rs-select-wrapper">
            <select
              value={selectedRunId}
              onChange={e => setSelectedRunId(e.target.value)}
              disabled={!selectedAppName || runsLoading || projectRuns.length === 0}
            >
              <option value="">
                {runsLoading
                  ? 'Loading…'
                  : !selectedAppName
                    ? '—'
                    : projectRuns.length === 0
                      ? 'No runs'
                      : 'Select run…'}
              </option>
              {projectRuns.map((run, i) => (
                <option key={run.workflow_run_id} value={run.workflow_run_id}>
                  {runLabel(run, i)}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="rs-select-icon" />
          </div>
        </div>

        {runsLoading && <Loader2 size={15} className="rs-spinner spin" />}
        {runsError   && <AlertCircle size={15} className="rs-error-icon" title={runsError} />}

        {/* Journey Run selector — only shown when HITL jobs exist for this project */}
        {hitlJobs.length > 0 && (
          <>
            <span className="rs-separator">·</span>
            <div className="rs-selector-group">
              <label className="rs-hitl-label">Journey Run</label>
              <div className="rs-select-wrapper">
                <select
                  value={selectedHitlJobId}
                  onChange={e => {
                    const chosen = hitlJobs.find(j => j.job_id === e.target.value)
                    setSelectedHitlJobId(e.target.value)
                    setHitlLinkedJobs(chosen?.linked_jobs || {})
                    setActiveView('hitl-overview')
                    setActiveNavItem('hitl-overview')
                    setViewContext(null)
                  }}
                  disabled={hitlJobsLoading}
                >
                  {hitlJobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>{j.label}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="rs-select-icon" />
              </div>
            </div>
          </>
        )}
        {hitlJobsLoading && <Loader2 size={15} className="rs-spinner spin" />}
      </div>

      {/* ── Body: sidebar + main ──────────────────────────────────── */}
      <div className="reports-body">

        {/* Left sidebar */}
        <div className={`reports-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {!sidebarCollapsed && (
            <div className="sidebar-inner">
              <div className="sidebar-heading">Reports</div>

              {REPORT_NAV_GROUPS.map(group => {
                const GroupIcon = group.icon
                const isExpanded = expandedGroups[group.id]
                return (
                  <div key={group.id} className="nav-group">
                    <button
                      className="nav-group-header"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <GroupIcon size={13} />
                      <span>{group.label}</span>
                      <ChevronRight
                        size={11}
                        className={`group-chevron ${isExpanded ? 'expanded' : ''}`}
                      />
                    </button>

                    {isExpanded && (
                      <div className="nav-group-items">
                        {group.items.map(item => {
                          const available = hasRunSelection && !!getJobForType(item.requiredJob || item.id)
                          const isActive  = activeNavItem === item.id
                          return (
                            <button
                              key={item.id}
                              className={`nav-item ${available ? 'available' : 'unavailable'} ${isActive ? 'active' : ''}`}
                              onClick={() => available && handleReportClick(item)}
                              disabled={!available}
                              title={
                                !hasProjectSelection ? 'Select a project first' :
                                !hasRunSelection     ? 'Select a run first' :
                                !available           ? 'No data for this report in the selected run' :
                                item.label
                              }
                            >
                              <span className={`nav-dot ${available ? 'on' : ''}`} />
                              {item.label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Journey nav group — shown when HITL jobs exist ── */}
          {hitlJobs.length > 0 && !sidebarCollapsed && (
            <div className="nav-group">
              <button
                className="nav-group-header"
                onClick={() => toggleGroup('journey')}
              >
                <Layers size={13} />
                <span>Journey</span>
                <ChevronRight
                  size={11}
                  className={`group-chevron ${expandedGroups['journey'] !== false ? 'expanded' : ''}`}
                />
              </button>
              {expandedGroups['journey'] !== false && (
                <div className="nav-group-items">
                  {JOURNEY_NAV_ITEMS.map(item => {
                    // Determine availability
                    let available = true
                    if (item.linkedJobKey === 'ca_job_id')        available = !!hitlLinkedJobs.ca_job_id
                    if (item.linkedJobKey === 'ai_naming_job_id') available = !!hitlLinkedJobs.ai_naming_job_id
                    if (item.linkedJobKey === 'dm_job_id')        available = !!hitlLinkedJobs.dm_job_id
                    // hitl-overview and null-linked always available when HITL job selected
                    if (item.view === 'hitl-overview')            available = !!selectedHitlJobId
                    const isActive = activeNavItem === item.id
                    return (
                      <button
                        key={item.id}
                        className={`nav-item ${available ? 'available' : 'unavailable'} ${isActive ? 'active' : ''}`}
                        onClick={() => available && handleJourneyReportClick(item)}
                        disabled={!available}
                        title={!available ? 'No data available for this report' : item.label}
                      >
                        <span className={`nav-dot ${available ? 'on' : ''}`} />
                        {item.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Collapse toggle */}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {sidebarCollapsed
              ? <PanelLeftOpen  size={15} />
              : <PanelLeftClose size={15} />
            }
          </button>
        </div>

        {/* Main content */}
        <div className="reports-main">

          {/* ── HITL Journey Overview ── */}
          {activeView === 'hitl-overview' && selectedHitlJobId ? (
            <div className="reports-embedded-view">
              <HitlOverviewReport
                rcJobId={selectedHitlJobId}
                apiBase={getAPIBaseUrl()}
                accountId={selectedAccountId}
                appName={selectedAppName}
              />
            </div>

          ) : activeView && VIEW_COMPONENTS[activeView] ? (
            (() => {
              const ViewComponent = VIEW_COMPONENTS[activeView]
              return (
                <div className="reports-embedded-view">
                  <ViewComponent onBack={handleBack} analysisContext={viewContext} />
                </div>
              )
            })()

          ) : !selectedAccountId || !selectedAppName ? (
            <div className="rm-empty">
              <FileBarChart size={40} strokeWidth={1.5} />
              <h3>Select a Project</h3>
              <p>Choose an account and project to view available reports.</p>
            </div>

          ) : runsLoading ? (
            <div className="rm-empty">
              <Loader2 size={32} className="spin" />
              <p>Loading runs…</p>
            </div>

          ) : runsError ? (
            <div className="rm-empty">
              <AlertCircle size={32} />
              <p>{runsError}</p>
            </div>

          ) : projectRuns.length === 0 && hitlJobs.length === 0 ? (
            <div className="rm-empty">
              <Database size={40} strokeWidth={1.5} />
              <h3>No Runs Found</h3>
              <p>No workflow runs found for <strong>{selectedAppName}</strong>.</p>
              <span className="rm-hint">Run a workflow from the Workflow tab to generate reports.</span>
            </div>

          ) : !selectedRunId ? (
            <div className="rm-empty">
              <Play size={40} strokeWidth={1.5} />
              <h3>Select a Run</h3>
              <p>Choose a workflow run from the selector above.</p>
            </div>

          ) : (
            /* Run selected — Run Overview Dashboard */
            <RunOverviewDashboard
              accountId={selectedAccountId}
              appName={selectedAppName}
              workflowRunId={selectedRun?.workflow_run_id}
              onReportClick={handleReportClick}
              reportGroups={REPORT_NAV_GROUPS}
              getJobForType={getJobForType}
            />
          )}
        </div>
      </div>

      {/* Workspace Assistant */}
      <AIAssistant
        account={selectedAccountId}
        application={selectedAppName}
        run={selectedRun}
        pageType="reports"
        pageContext={{
          active_report: activeView || null,
          selected_run_status: selectedRun?.status || null,
        }}
      />
    </div>
  )
}

export default ReportsView
